import { Component, inject } from "@angular/core";
import { NgGlyph } from "@ng-icons/core";
import { AppAvatarComponent } from "../../../../Common/Components/Avatar/AvatarComponent";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";

interface TealSwatch {
  name: string;
  variable: string;
  hex: string;
}

interface ColorSection {
  title: string;
  swatches: TealSwatch[];
}

interface SurfaceToken {
  name: string;
  variable: string;
  description: string;
}

interface ElevationToken {
  name: string;
  variable: string;
  description: string;
}

interface TypographyToken {
  variable: string;
  description: string;
  className?: string;
  lineHeightVariable?: string;
  fontWeightVariable?: string;
  letterSpacingVariable?: string;
}

@Component({
  standalone: true,
  selector: "app-design-page",
  imports: [NgGlyph, AppAvatarComponent, AppButtonComponent, LocationSelectorComponent],
  templateUrl: "./DesignPage.html",
  styleUrl: "./DesignPage.scss",
})
export class DesignPage {
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly toastEffects = inject(ToastEffects);

  locationValue: LocationAddress = {
    stateProvinceRegion: "",
    countyDistrict: "",
    subdistrictMunicipality: "",
    communeVillage: "",
    line: "",
    zip: "",
    longitude: 0,
    latitude: 0,
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onLocationValueChange(_value: LocationAddress): void {
    // Value updated via two-way binding through the input
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAddressChange(value: LocationAddress): void {
    // Can be used for additional handling if needed
  }

  openConfirmModal(variant: "primary" | "danger"): void {
    const isDanger = variant === "danger";
    this.confirmModalEffects.open({
      title: (() => {
        if (isDanger) {
          return "Delete this item?";
        }
        return "Confirm action";
      })(),
      message: (() => {
        if (isDanger) {
          return "This action will permanently remove the item and cannot be undone.";
        }
        return "This action will apply the changes to the selected item.";
      })(),
      confirmText: (() => {
        if (isDanger) {
          return "Delete";
        }
        return "Confirm";
      })(),
      cancelText: "Cancel",
      variant,
      closeOnOverlay: true,
    });
  }

  showToast(variant: "success" | "error" | "warning" | "info"): void {
    const messageMap = {
      success: "Success! Changes saved.",
      error: "Something went wrong. Please try again.",
      warning: "Heads up! Review this before continuing.",
      info: "FYI: This is an informational toast.",
    };
    this.toastEffects.show(messageMap[variant], variant);
  }
  iconSamples = [
    { name: "search", label: "Search" },
    { name: "settings", label: "Settings" },
    { name: "notifications", label: "Notifications" },
    { name: "mail", label: "Inbox" },
    { name: "edit", label: "Edit" },
    { name: "delete", label: "Delete" },
    { name: "add", label: "Add" },
    { name: "remove", label: "Remove" },
    { name: "close", label: "Close" },
    { name: "check", label: "Check" },
    { name: "arrow_back", label: "Back" },
    { name: "arrow_forward", label: "Forward" },
    { name: "expand_more", label: "Expand" },
    { name: "refresh", label: "Refresh" },
    { name: "filter_list", label: "Filter" },
    { name: "tune", label: "Tune" },
    { name: "sort", label: "Sort" },
    { name: "visibility", label: "View" },
    { name: "visibility_off", label: "Hide" },
    { name: "download", label: "Download" },
    { name: "upload", label: "Upload" },
    { name: "share", label: "Share" },
    { name: "link", label: "Link" },
    { name: "calendar_today", label: "Calendar" },
    { name: "schedule", label: "Schedule" },
    { name: "person", label: "Profile" },
    { name: "group", label: "Users" },
    { name: "lock", label: "Lock" },
    { name: "help", label: "Help" },
    { name: "info", label: "Info" },
    { name: "home", label: "Home" },
    { name: "dashboard", label: "Dashboard" },
    { name: "menu", label: "Menu" },
    { name: "more_horiz", label: "More" },
    { name: "more_vert", label: "More vertical" },
    { name: "language", label: "Language" },
    { name: "location_on", label: "Location" },
    { name: "star", label: "Star" },
    { name: "star_border", label: "Star outline" },
    { name: "favorite", label: "Favorite" },
    { name: "favorite_border", label: "Favorite outline" },
    { name: "warning", label: "Warning" },
    { name: "error", label: "Error" },
    { name: "check_circle", label: "Check circle" },
    { name: "cancel", label: "Cancel" },
    { name: "save", label: "Save" },
    { name: "print", label: "Print" },
    { name: "credit_card", label: "Payment" },
    { name: "attach_file", label: "Attachment" },
    { name: "folder", label: "Folder" },
    { name: "folder_open", label: "Folder open" },
    { name: "cloud", label: "Cloud" },
    { name: "cloud_download", label: "Cloud download" },
    { name: "cloud_upload", label: "Cloud upload" },
    { name: "camera_alt", label: "Camera" },
    { name: "image", label: "Image" },
    { name: "map", label: "Map" },
    { name: "person", label: "Person" },
    { name: "chat", label: "Chat" },
    { name: "send", label: "Send" },
  ];
  // Color palettes to render test swatches for.
  sections: ColorSection[] = [
    {
      title: "Red",
      swatches: [
        { name: "Red 100", variable: "--color-red-100", hex: "#ffe4db" },
        { name: "Red 200", variable: "--color-red-200", hex: "#ffc9b7" },
        { name: "Red 300", variable: "--color-red-300", hex: "#ffad92" },
        { name: "Red 400", variable: "--color-red-400", hex: "#ff926e" },
        { name: "Red 500", variable: "--color-red-500", hex: "#ff5722" },
        { name: "Red 600", variable: "--color-red-600", hex: "#e64e1f" },
        { name: "Red 700", variable: "--color-red-700", hex: "#cc451b" },
        { name: "Red 800", variable: "--color-red-800", hex: "#b33c18" },
        { name: "Red 900", variable: "--color-red-900", hex: "#993314" },
      ],
    },
    {
      title: "Orange",
      swatches: [
        { name: "Orange 100", variable: "--color-orange-100", hex: "#fff1df" },
        { name: "Orange 200", variable: "--color-orange-200", hex: "#ffe0ba" },
        { name: "Orange 300", variable: "--color-orange-300", hex: "#ffc994" },
        { name: "Orange 400", variable: "--color-orange-400", hex: "#ffb26e" },
        { name: "Orange 500", variable: "--color-orange-500", hex: "#ff9323" },
        { name: "Orange 600", variable: "--color-orange-600", hex: "#e5811c" },
        { name: "Orange 700", variable: "--color-orange-700", hex: "#cb7016" },
        { name: "Orange 800", variable: "--color-orange-800", hex: "#b05e0f" },
        { name: "Orange 900", variable: "--color-orange-900", hex: "#964d09" },
      ],
    },
    {
      title: "Yellow",
      swatches: [
        { name: "Yellow 100", variable: "--color-yellow-100", hex: "#fff9cc" },
        { name: "Yellow 200", variable: "--color-yellow-200", hex: "#fff399" },
        { name: "Yellow 300", variable: "--color-yellow-300", hex: "#ffec66" },
        { name: "Yellow 400", variable: "--color-yellow-400", hex: "#ffe633" },
        { name: "Yellow 500", variable: "--color-yellow-500", hex: "#ffde00" },
        { name: "Yellow 600", variable: "--color-yellow-600", hex: "#e5c800" },
        { name: "Yellow 700", variable: "--color-yellow-700", hex: "#ccb200" },
        { name: "Yellow 800", variable: "--color-yellow-800", hex: "#b39c00" },
        { name: "Yellow 900", variable: "--color-yellow-900", hex: "#9a8600" },
      ],
    },
    {
      title: "Green",
      swatches: [
        { name: "Green 100", variable: "--color-green-100", hex: "#def6e3" },
        { name: "Green 200", variable: "--color-green-200", hex: "#bcecc7" },
        { name: "Green 300", variable: "--color-green-300", hex: "#9be2ab" },
        { name: "Green 400", variable: "--color-green-400", hex: "#79d98f" },
        { name: "Green 500", variable: "--color-green-500", hex: "#4caf50" },
        { name: "Green 600", variable: "--color-green-600", hex: "#449d48" },
        { name: "Green 700", variable: "--color-green-700", hex: "#3c8b40" },
        { name: "Green 800", variable: "--color-green-800", hex: "#347a38" },
        { name: "Green 900", variable: "--color-green-900", hex: "#2c6830" },
      ],
    },
    {
      title: "Teal",
      swatches: [
        { name: "Teal 100", variable: "--color-teal-100", hex: "#ccfbf4" },
        { name: "Teal 200", variable: "--color-teal-200", hex: "#99f7e9" },
        { name: "Teal 300", variable: "--color-teal-300", hex: "#66f2de" },
        { name: "Teal 400", variable: "--color-teal-400", hex: "#33eed3" },
        { name: "Teal 500", variable: "--color-teal-500", hex: "#00cda6" },
        { name: "Teal 600", variable: "--color-teal-600", hex: "#00b492" },
        { name: "Teal 700", variable: "--color-teal-700", hex: "#009c7f" },
        { name: "Teal 800", variable: "--color-teal-800", hex: "#00856d" },
        { name: "Teal 900", variable: "--color-teal-900", hex: "#00705c" },
      ],
    },
    {
      title: "Blue",
      swatches: [
        { name: "Blue 100", variable: "--color-blue-100", hex: "#e8eefc" },
        { name: "Blue 200", variable: "--color-blue-200", hex: "#d1ddf9" },
        { name: "Blue 300", variable: "--color-blue-300", hex: "#a4bbf3" },
        { name: "Blue 400", variable: "--color-blue-400", hex: "#779aec" },
        { name: "Blue 500", variable: "--color-blue-500", hex: "#6386df" },
        { name: "Blue 600", variable: "--color-blue-600", hex: "#5572c2" },
        { name: "Blue 700", variable: "--color-blue-700", hex: "#475fa5" },
        { name: "Blue 800", variable: "--color-blue-800", hex: "#394c88" },
        { name: "Blue 900", variable: "--color-blue-900", hex: "#2b396b" },
      ],
    },
    {
      title: "Purple",
      swatches: [
        { name: "Purple 100", variable: "--color-purple-100", hex: "#f0e6fb" },
        { name: "Purple 200", variable: "--color-purple-200", hex: "#e1cdf7" },
        { name: "Purple 300", variable: "--color-purple-300", hex: "#d2b5f2" },
        { name: "Purple 400", variable: "--color-purple-400", hex: "#c39ced" },
        { name: "Purple 500", variable: "--color-purple-500", hex: "#673ab7" },
        { name: "Purple 600", variable: "--color-purple-600", hex: "#5c33a4" },
        { name: "Purple 700", variable: "--color-purple-700", hex: "#512c91" },
        { name: "Purple 800", variable: "--color-purple-800", hex: "#46257e" },
        { name: "Purple 900", variable: "--color-purple-900", hex: "#3a1e6b" },
      ],
    },
  ];

  surfaces: SurfaceToken[] = [
    {
      name: "Base",
      variable: "--surface-base",
      description: "Default page background for main content areas.",
    },
    {
      name: "Raised",
      variable: "--surface-raised",
      description: "Elevated surfaces like cards and panels.",
    },
    {
      name: "Sunken",
      variable: "--surface-sunken",
      description: "Inset surfaces such as input wells or secondary panels.",
    },
    {
      name: "Muted",
      variable: "--surface-muted",
      description: "Quiet surface for subtle separation.",
    },
    {
      name: "Border",
      variable: "--surface-border",
      description: "Default border color for surfaces.",
    },
    {
      name: "Border strong",
      variable: "--surface-border-strong",
      description: "Stronger border for emphasis.",
    },
    {
      name: "Hover",
      variable: "--surface-hover",
      description: "Surface fill for hover state.",
    },
    {
      name: "Active",
      variable: "--surface-active",
      description: "Surface fill for active state.",
    },
  ];

  elevations: ElevationToken[] = [
    {
      name: "Elevation 1",
      variable: "--elevation-1",
      description: "Default card shadow for subtle lift.",
    },
    {
      name: "Elevation 2",
      variable: "--elevation-2",
      description: "Stronger shadow for raised panels.",
    },
    {
      name: "Elevation 3",
      variable: "--elevation-3",
      description: "Highest elevation for overlays or popovers.",
    },
  ];

  fontSizes: TypographyToken[] = [
    {
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      variable: "--font-size-3xl",
    },
    {
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      variable: "--font-size-2xl",
    },
    {
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      variable: "--font-size-xl",
    },
    {
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      variable: "--font-size-lg",
    },
    {
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      variable: "--font-size-md",
    },
    {
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      variable: "--font-size-sm",
    },
    {
      description: "Lorem ipsum dolor sit amet.",
      variable: "--font-size-xs",
    },
  ];

  fontWeights: TypographyToken[] = [
    {
      variable: "--font-weight-bold",
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      fontWeightVariable: "--font-weight-bold",
    },
    {
      variable: "--font-weight-semibold",
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      fontWeightVariable: "--font-weight-semibold",
    },
    {
      variable: "--font-weight-medium",
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      fontWeightVariable: "--font-weight-medium",
    },
    {
      variable: "--font-weight-regular",
      description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
      fontWeightVariable: "--font-weight-regular",
    },
  ];

  lineHeights: TypographyToken[] = [
    {
      variable: "--line-height-tight",
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      lineHeightVariable: "--line-height-tight",
    },
    {
      variable: "--line-height-normal",
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      lineHeightVariable: "--line-height-normal",
    },
    {
      variable: "--line-height-loose",
      description:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      lineHeightVariable: "--line-height-loose",
    },
  ];

  letterSpacings: TypographyToken[] = [
    {
      variable: "--letter-spacing-tight",
      description: "Lorem ipsum dolor sit amet.",
      letterSpacingVariable: "--letter-spacing-tight",
    },
    {
      variable: "--letter-spacing-normal",
      description: "Lorem ipsum dolor sit amet.",
      letterSpacingVariable: "--letter-spacing-normal",
    },
    {
      variable: "--letter-spacing-wide",
      description: "Lorem ipsum dolor sit amet.",
      letterSpacingVariable: "--letter-spacing-wide",
    },
  ];
}
